import {
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Box,
  Divider,
  Grid,
} from "@mui/material";


import { Formik, Form } from "formik";
import { useState } from "react";
import { postProject } from "../../api/project";
import formModel from "./formModel";
import ProjectSavedPage from "./WizardSteps/ProjectSavedPage";
import ProjectSuccessPage from "./WizardSteps/ProjectSuccessPage";
import { useTranslation } from "react-i18next";
import AreYouSureDialog from "../Reuseables/Dialogs/AreYouSureDialog";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";
import { Project } from "../../types/project";
import { Stage } from "../../types/enums";
import { CustomStepConnector, CreateProjectStepper } from "../Reuseables/CustomStepper";
import { GetWizard } from "./WizardHelper";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";

const { formId } = formModel;

export default function ProjectWizard() {
  const { activeWorkspace ,updateProject} = useOrganizationContext();

  const [activeStep, setActiveStep] = useState(0);
  const [projectSuccess, setProjectSuccess] = useState(false);
  const [projectSaved, setProjectSaved] = useState(false);
  const [newProject, setNewProject] = useState(null);
  const [areYouSure, setAreYouSure] = useState(false);
  const { showSnackbar, showLoading, stopLoading } = useApplicationContext();
  const { t } = useTranslation();

  const wizard: any = GetWizard(activeWorkspace, t);

  const isLastStep = activeStep === wizard.steps.length - 1;

  async function _submitForm(values: Project, actions: any): Promise<void> {
    try {
      let project = await postProject(values);
      setNewProject(project);
      if (project.stage === Stage.Preparation) {
        setProjectSaved(true);
        updateProject(project);
      } else {
        setProjectSuccess(true);
        updateProject(project);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        actions.setSubmitting(false);
        showSnackbar(error.message, "error");
        setAreYouSure(false);
      } else {
        actions.setSubmitting(false);
        showSnackbar("Unknown error", "error");
        setAreYouSure(false);
      }
    } finally {
      stopLoading();
    }
  }

  function handleCloseDialog() {
    setAreYouSure(false);
  }

  function _isProjectSaved() {
    if (projectSuccess) {
      return <ProjectSuccessPage project={newProject} />;
    }
    if (projectSaved) {
      return <ProjectSavedPage project={newProject} />;
    }
  }

  function _handleSubmit(values: Project, actions: any) {
    if (isLastStep) {
      actions.setSubmitting(false);
      setAreYouSure(true);
    } else if (values.skipActivities) {
      showLoading("loading.savingProject");
      _submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }

  function _handleBack() {
    setActiveStep(activeStep - 1);
  }

  type Step = {
    label: string;
  };

  

  return (
    <>
      <>
        {projectSuccess || projectSaved ? (
          _isProjectSaved()
        ) : (
          <>
            <Stepper
              connector={<CustomStepConnector />}
              alternativeLabel
              activeStep={activeStep}
            >
              {wizard.steps.map((step: Step) => (
                <Step key={step.label}>
                  <StepLabel StepIconComponent={CreateProjectStepper}>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <>
              <Formik
                initialValues={wizard.initialValues}
                validationSchema={wizard.steps[activeStep].validationScheme}
                onSubmit={_handleSubmit}
              >
                {({ isSubmitting, values, setSubmitting }) => (
                  <Form
                    autoComplete="off"
                    style={{
                      width: "100%",
                      marginTop: "20px",
                    }}
                    id={formId}
                  >
                    <Grid container minHeight="60vh" mb={2}>
                      {wizard.steps[activeStep].component}
                    </Grid>
                    <Divider />

                    <Grid container mt={3}>
                      <Grid item sx={{ flexGrow: 1 }} />
                      <Grid item md={4} justifyContent="space-between">
                        <Box display={"flex"} justifyContent={"space-between"}>
                          <Button
                            disabled={activeStep === 0 || isSubmitting}
                            onClick={_handleBack}
                          >
                            {t("buttons.back")}
                          </Button>
                          <Button
                            disabled={isSubmitting}
                            type="submit"
                            variant="contained"
                          >
                            {isLastStep
                              ? t("buttons.submit")
                              : t("buttons.next")}
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item sx={{ flexGrow: 1 }}></Grid>
                    </Grid>
                    <AreYouSureDialog
                      handleClose={handleCloseDialog}
                      isDialogOpen={areYouSure}
                      onSubmit={() => {
                        setSubmitting(true);
                        //Quick fix to setSubmitting to false
                        let actions = { setSubmitting };
                        showLoading(t("loading.creatingProject"));
                        _submitForm(values, actions);
                        handleCloseDialog();
                      }}
                      title={t("dialogs.areYouSureHeader")}
                      content={
                        <Typography>{t("dialogs.areYouSureText")}</Typography>
                      }
                      isSubmitting={isSubmitting}
                    />
                  </Form>
                )}
              </Formik>
            </>
          </>
        )}
      </>
    </>
  );
}




